import { paramsToQuery } from '@/base/MaUtils.js'
import { cacheRequest, makeRequest } from '@/base/MaRequestPooler.js'
import Device from '@/base/constants/Device.js'

/**
 *
 * @param urlParams
 * - trackId: Searched trackId
 * - countryCode: default to us
 * @param queryParams
 * - startDate: date in string format MaUtils.ISO_DATE_FORMAT
 * - endDate: date in string format MaUtils.ISO_DATE_FORMAT
 * - pageSize
 * - currentPage
 * - sortColumn
 * - order
 * - keywordFilter
 * - minPopularity
 * - maxPopularity
 * - minPercentage
 * - maxPercentage
 */
export function fetchAppStatisticsWithFilters({ trackId, countryCode }, queryParams) {
  let query = ''
  if (queryParams && Object.keys(queryParams).length) {
    query = Object.keys(queryParams)
      .reduce((q, param) => (`${(q === '') ? '?' : (`${q}&`)}${param}=${queryParams[param]}`), '')
  }
  return cacheRequest(`/sa/stats/app/${trackId}/${countryCode}${query}`)
}

export function fetchSearchAdsImpression({ trackId, countryCode }) {
  return cacheRequest(`/app/${trackId}/sa-stats/${countryCode}`)
}

/**
 *
 * @param urlParams
 * - keyword: keyword string
 * - countryCode: default to us
 * - startDate: date in string format ISO_DATE_FORMAT
 * - endDate: date in string format ISO_DATE_FORMAT
 */
export function fetchKeywordStatistic({ keyword, countryCode, startDate, endDate }) {
  return cacheRequest(`/sa/stats/keyword/${keyword}/${countryCode}?startDate=${startDate}&endDate=${endDate}`)
}

/**
 *
 * @param urlParams
 * - keyword: keyword string
 * - countryCode: default to us
 * - startDate: date in string format ISO_DATE_FORMAT
 * - endDate: date in string format ISO_DATE_FORMAT
 */
export function getSearchAdsTopAd({ keyword, countryCode, startDate, endDate }) {
  return makeRequest(`keyword/sa-stats/${countryCode}?${paramsToQuery({
    startDate,
    endDate,
  })}`, {
    method: 'POST',
    data: { keyword },
  })
}

/**
 * Returns keyword rankings for selected countryCode for given app on iOS platform.
 * @param urlParams
 * {
 *  trackId: trackId of selected app, long
 *  countryCode: country code, string
 *  device: device of selected app, string
 *  keywords: Array of String keywords
 * }
 */
export function fetchSAKeywordRankings({ trackId, countryCode, device = Device.Iphone, keywords }) {
  const query = keywords.map(keyword => `keywords=${encodeURIComponent(keyword)}`).join('&')

  return makeRequest(`/sa/stats/keyword-ranking/app/${trackId}/${countryCode}/${device}?${query}`)
}

/**
 * Returns keyword rankings for selected countryCode for given app on Android platform.
 * @param urlParams
 * {
 *  trackId: trackId of selected app, long
 *  countryCode: country code, string
 *  device: device of selected app, string
 *  keywords: Array of String keywords
 * }
 */
export function fetchUACKeywordRankings({ trackId, countryCode, keywords }) {
  const query = keywords.map(keyword => `keywords=${encodeURIComponent(keyword)}`).join('&')

  return makeRequest(`/uac/stats/keyword-ranking/app/${trackId}/${countryCode}?${query}`)
}

/**
 * Returns active countries for selected app's running search ads
 * @param urlParams
 * {
 *  trackId: trackId of selected app, long
 * }
 */
export function fetchActiveCountries({ trackId }) {
  return makeRequest(`/sa/stats/scores/${trackId}/countries`)
}

/**
 * Returns active countries for selected app's running search ads
 * @param urlParams
 * {
 *  trackId: trackId of selected app, long
 *  startDate: date in string format ISO_DATE_FORMAT
 *  endDate: date in string format ISO_DATE_FORMAT
 * }
 */
export function fetchActiveCountriesWithScore({ trackId, startDate, endDate }) {
  return makeRequest(`/sa/stats/scores/${trackId}/countries-with-score/?${paramsToQuery({
    startDate,
    endDate,
  })}`)
}

/**
 * Fetches the top advertisers based on the provided filters.
 *
 * @param {object} options - The options for fetching top advertisers.
 * @param {string} options.countryCode - The country code to filter advertisers (e.g., 'US' for the United States).
 * @param {string} options.date - The date for the ranking data in 'YYYY-MM-DD' format.
 * @param {string} [options.category] - The categoryId to filter advertisers (optional).
 * @param {string} [options.rankings] - The type of rankings to retrieve (e.g., 'FREE', 'GROSSING' or 'PAID') (optional).
 * @param {string} [options.device] - The device type (e.g., 'iphone' or 'ipad') (optional).
 * @param {number} [options.limit] - The maximum number of advertisers to retrieve (optional), default 1000.
 *
 * @returns {Promise<Array>} - A promise that resolves with the top advertisers data.
 */
export function fetchTopAdvertisers({ countryCode, date, category, rankings, device, limit, isTaxonomy, subGenreIdList }) {
  return makeRequest(`/top-advertiser/v2/country/${countryCode}?${paramsToQuery({
    rankings,
    device,
    category,
    date,
    limit,
    isTaxonomy,
    subGenreIdList,
  })}`)
}

/**
 * Returns search ads visibility score for selected app, country and date
 * @param urlParams
 * {
 *  trackId: trackId of selected app, long
 *  countryCode: country code, string
 *  startDate: date in string format ISO_DATE_FORMAT
 *  endDate: date in string format ISO_DATE_FORMAT
 * }
 */
export function fetchAppVisibilityScoreWithDate({ trackId, countryCode, startDate, endDate }) {
  return cacheRequest(`/sa/stats/scores/${countryCode}/${trackId}/score?${paramsToQuery({ startDate, endDate })}`)
}

/**
 * Returns search ads visibility score and keyword count for selected app and country
 * @param urlParams
 * {
 * - trackId: trackId of selected app, long
 * - countryCode: country code, string
 * }
 */
export function searchAdsVisibilityScoreAndKwCount({ trackIds, countryCode, date }) {
  return cacheRequest(`/sa/stats/apps-score-and-kw-count/${countryCode}?${paramsToQuery({
    trackIds,
    countryCode,
    date,
  })}`)
}

/**
 * Returns search ads visibility score for selected app and country at given date range
 * @param urlParams
 * {
 * - trackId: trackId of selected app, long
 * - countryCode: country code, string
 * }
 */
export function searchAdsVisibilityScoreHistory({ trackId, countryCode, startDate, endDate }) {
  return cacheRequest(`sa/stats/scores/${countryCode}/${trackId}/history?${paramsToQuery({
    startDate,
    endDate,
  })}`)
}

export function getAdIntelligenceSummary({ trackId }) {
  return makeRequest(`/ad-intelligence/summary/app/${trackId}`)
}

/**
 *
 * @param urlParams
 * - keyword: keyword string
 * - countryCode: default to us
 * - startDate: date in string format ISO_DATE_FORMAT
 * - endDate: date in string format ISO_DATE_FORMAT
 */
export function fetchKeywordWeeklyImpressionShare({ keyword, countryCode, startDate, endDate }) {
  return makeRequest(`/sa/stats/keyword-weekly/${keyword}/${countryCode}?startDate=${startDate}&endDate=${endDate}`)
}

/**
 * Returns mutual keywords for selected app and competitor app
 * @param urlParams
 * {
 * - trackId: trackId of selected app, long
 * - competitorId: trackId of selected competitor app, long
 * - countryCode: country code, string
 * }
 */
export function fetchMutualKeywords({ trackId, competitorId, countryCode }, queryParams) {
  let query = ''
  if (queryParams && Object.keys(queryParams).length) {
    query = Object.keys(queryParams)
      .reduce((q, param) => (`${(q === '') ? '?' : (`${q}&`)}${param}=${queryParams[param]}`), '')
  }
  return makeRequest(`/sa/stats/mutual-app-stats/v2/${trackId}/competitor/${competitorId}/${countryCode}${query}`)
}

/**
 * Returns mutual keywords for selected app and competitor app
 * @param urlParams
 * {
 * - trackId: trackId of selected app, long
 * - competitorId: trackId of selected competitor app, long
 * - countryCode: country code, string
 * }
 */
export function fetchMainKeywords({ trackId, competitorId, countryCode }, queryParams) {
  let query = ''
  if (queryParams && Object.keys(queryParams).length) {
    query = Object.keys(queryParams)
      .reduce((q, param) => (`${(q === '') ? '?' : (`${q}&`)}${param}=${queryParams[param]}`), '')
  }
  return makeRequest(`/sa/stats/non-mutual-app-stats/${trackId}/competitor/${competitorId}/${countryCode}${query}`)
}

/**
 * Returns mutual keywords for selected app and competitor app
 * @param urlParams
 * {
 * - trackId: trackId of selected app, long
 * - competitorId: trackId of selected competitor app, long
 * - countryCode: country code, string
 * }
 */
export function fetchCompetitorKeywords({ trackId, competitorId, countryCode }, queryParams) {
  let query = ''
  if (queryParams && Object.keys(queryParams).length) {
    query = Object.keys(queryParams)
      .reduce((q, param) => (`${(q === '') ? '?' : (`${q}&`)}${param}=${queryParams[param]}`), '')
  }
  return makeRequest(`/sa/stats/non-mutual-app-stats/${competitorId}/competitor/${trackId}/${countryCode}${query}`)
}

/**
 * Returns mutual keywords count for selected app and competitor app
 * @param urlParams
 * {
 * - trackId: trackId of selected app, long
 * - competitorId: trackId of selected competitor app, long
 * - countryCode: country code, string
 * }
 */
export function fetchMutualKeywordsCount({ trackId, competitorId, countryCode }, queryParams) {
  let query = ''
  if (queryParams && Object.keys(queryParams).length) {
    query = Object.keys(queryParams)
      .reduce((q, param) => (`${(q === '') ? '?' : (`${q}&`)}${param}=${queryParams[param]}`), '')
  }
  return makeRequest(`/sa/stats/app-mutual-count/${trackId}/competitor/${competitorId}/${countryCode}${query}`)
}

/**
 * Returns main app keywords count for selected app and competitor app
 * @param urlParams
 * {
 * - trackId: trackId of selected app, long
 * - competitorId: trackId of selected competitor app, long
 * - countryCode: country code, string
 * }
 */
export function fetchMainKeywordsCount({ trackId, competitorId, countryCode }, queryParams) {
  let query = ''
  if (queryParams && Object.keys(queryParams).length) {
    query = Object.keys(queryParams)
      .reduce((q, param) => (`${(q === '') ? '?' : (`${q}&`)}${param}=${queryParams[param]}`), '')
  }
  return makeRequest(`/sa/stats/app-non-mutual-count/${trackId}/competitor/${competitorId}/${countryCode}${query}`)
}

/**
 * Returns competitor app keywords count for selected app and competitor app
 * @param urlParams
 * {
 * - trackId: trackId of selected app, long
 * - competitorId: trackId of selected competitor app, long
 * - countryCode: country code, string
 * }
 */
export function fetchCompetitorKeywordsCount({ trackId, competitorId, countryCode }, queryParams) {
  let query = ''
  if (queryParams && Object.keys(queryParams).length) {
    query = Object.keys(queryParams)
      .reduce((q, param) => (`${(q === '') ? '?' : (`${q}&`)}${param}=${queryParams[param]}`), '')
  }
  return makeRequest(`/sa/stats/app-non-mutual-count/${competitorId}/competitor/${trackId}/${countryCode}${query}`)
}

/**
 * Returns app store product pages tab ads for selected app.
 * @param urlParams
 * {
 * - trackId: trackId of selected app, long
 * - countryCode: country code, string
 * - date: date in string format ISO_DATE_FORMAT - (optional) Default; previous day of the api gateway service will be used
 * - target: 'advertiser' | 'publisher'
 * }
 */
export function fetchProductPagesTabAds({ trackId, countryCode, date, target }) {
  return makeRequest(`/sa/stats/productpagetab/${target}/${trackId}/${countryCode}?date=${date}`)
}

/**
 * Returns app store today tab ads for selected country.
 * @param urlParams
 * {
 * - countryCode: country code, string
 * - date: date in string format ISO_DATE_FORMAT - (optional) Default; previous day of the api gateway service will be used
 * }
 */
export function fetchTodayTabAds({ countryCode, date }) {
  return makeRequest(`/sa/stats/todaytab/${countryCode}?date=${date}`)
}

/**
 * Returns app store search tab ads for selected country.
 * @param urlParams
 * {
 * - countryCode: country code, string
 * - date: date in string format ISO_DATE_FORMAT - (optional) Default; previous day of the api gateway service will be used
 * }
 */
export function fetchSearchTabAds({ countryCode, date }) {
  return makeRequest(`/sa/stats/searchtab/${countryCode}?date=${date}`)
}

/**
 * Fetches ASA paid activities for a given app based on provided parameters.
 *
 * @param {object} params - The parameters for fetching paid activities.
 * @param {number} params.trackId - The track ID of the app.
 * @param {string} params.countryCode - The country code to filter the results by.
 * @param {string} [params.spikeDirection] - The direction of the spike (optional).
 * @param {string} params.startDate - The start date for the fetch period in 'YYYY-MM-DD' format.
 * @param {string} params.endDate - The end date for the fetch period in 'YYYY-MM-DD' format.
 *
 * @returns {Promise<object>} A promise that resolves with the fetched data.
 */
export function fetchPaidActivities({ trackId, countryCode, spikeDirection, startDate, endDate }) {
  const query = paramsToQuery({ startDate, endDate, spikeDirection })
  return makeRequest(`sa/stats/paid-activities/app/${trackId}/country/${countryCode}?${query}`)
}

/**
 * Returns ad intel visibility score for multip app and multiple country date by date
 * @param urlParams
 * {
 *  trackId: trackId string
 *  countryCode: country code, string
 *  startDate: date in string format ISO_DATE_FORMAT
 *  endDate: date in string format ISO_DATE_FORMAT
 * }
 */
export function fetchAdIntelVisibilityScoreHistory({ trackId, countryCode, startDate, endDate }) {
  const query = paramsToQuery({ startDate, endDate })
  return makeRequest(`ad-intelligence/visibility-score-history/app/${trackId}/${countryCode}?${query}`)
}
